import React, { useEffect, useMemo, useRef, useState } from 'react';
import cl from 'classnames';
import { format } from 'date-fns/format';
import { OfflineHeadset } from 'src/models';
import { useLanguage } from 'src/hooks';
import { compareString } from 'src/helper';
import { ClipboardController } from 'src/components/general';
import styles from './copySelectStyle.module.scss';

const copyIcon = (
  <svg
    width="37"
    height="35"
    viewBox="0 0 37 35"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.icon}
  >
    <ellipse
      cx="18.4211"
      cy="17.5"
      rx="18.4211"
      ry="17.5"
      fill="white"
      fillOpacity="0.13"
    />
    <path
      d="M24.3996 15.5996H17.1996C16.316 15.5996 15.5996 16.316 15.5996 17.1996V24.3996C15.5996 25.2833 16.316 25.9996 17.1996 25.9996H24.3996C25.2833 25.9996 25.9996 25.2833 25.9996 24.3996V17.1996C25.9996 16.316 25.2833 15.5996 24.3996 15.5996Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.4 20.4H11.6C11.1757 20.4 10.7687 20.2314 10.4686 19.9314C10.1686 19.6313 10 19.2243 10 18.8V11.6C10 11.1757 10.1686 10.7687 10.4686 10.4686C10.7687 10.1686 11.1757 10 11.6 10H18.8C19.2243 10 19.6313 10.1686 19.9314 10.4686C20.2314 10.7687 20.4 11.1757 20.4 11.6V12.4"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const copyModes = ['numbered', 'unnumbered'] as const;

type CopySelectProps = {
  headsets: OfflineHeadset[];
  className?: string;
  isDisabled?: boolean | null;
};

export const CopySelect = ({
  headsets,
  className,
  isDisabled = false,
  ...otherProps
}: CopySelectProps &
  Partial<React.ButtonHTMLAttributes<HTMLButtonElement>>) => {
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const { t, td } = useLanguage();

  const disabled = useMemo(
    () => isDisabled || !headsets.length,
    [headsets.length, isDisabled],
  );

  const list = useMemo(
    () =>
      headsets
        .map((headset) => ({
          ...headset,
          roomLabel: headset.roomLabel ?? 'Н/Д',
        }))
        .sort((a, b) => {
          const byName = compareString(a.companyName, b.companyName);

          if (byName === 0) {
            const dateA = new Date(a.lastActivity).getTime();
            const dateB = new Date(b.lastActivity).getTime();
            return dateA - dateB;
          }

          return byName;
        })
        .map(
          ({ companyName, lastActivity, roomLabel }) =>
            `${companyName} – (был ${format(new Date(lastActivity), 'dd.MM.yy')}), ${roomLabel}`,
        ),
    [headsets],
  );
  const numberedText = list
    .map((item, index) => `${index + 1}) ${item}`)
    .join('\n');
  const unnumberedText = list.join('\n');

  useEffect(() => {
    const onDocumentClick = (event: Event) => {
      if (
        event.target instanceof Node &&
        listRef.current &&
        !listRef.current.contains(event.target)
      ) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener('click', onDocumentClick);
    }

    return () => {
      if (expanded) {
        document.removeEventListener('click', onDocumentClick);
      }
    };
  }, [expanded]);

  return (
    <button
      onClick={(event) => {
        event.stopPropagation();

        if (disabled) {
          return;
        }

        setExpanded((prevState) => !prevState);
      }}
      className={cl(styles.button, disabled && styles.disabled, className)}
      type="button"
      {...otherProps}
    >
      {copyIcon}
      <div className={styles.label}>
        {t('general.copy')}
        <svg
          width="14"
          height="16"
          viewBox="0 0 14 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cl(styles.arrowIcon, expanded && styles.expanded)}
        >
          <path
            d="M2.91536 10.5923L6.9987 6.05525L11.082 10.5923"
            stroke="#FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div
        className={cl(styles.list, expanded && styles.expanded)}
        onClick={(e) => {
          e.stopPropagation();
        }}
        ref={listRef}
      >
        <div style={{ minHeight: 0 }}>
          {copyModes.map((mode) => (
            <ClipboardController
              key={`copy-select-${mode}`}
              text={mode === 'numbered' ? numberedText : unnumberedText}
            >
              <div
                className={styles.listItem}
                onClick={() => {
                  setExpanded(false);
                }}
              >
                {td(`component.copySelect.${mode}`)}
              </div>
            </ClipboardController>
          ))}
        </div>
      </div>
    </button>
  );
};

export default CopySelect;
